/**
 * ``useStatusGeometrySseBridge`` — feeds the live ``status``-kind SSE
 * payloads' projected ``geometry`` into the per-platform geometry store
 * (PR5b inc3; REQ-UI-070; ADR-032 §4.2).
 *
 * The Map already receives status deltas (the snapshot layer merges them
 * for marker positions; the track bridge appends them to the track
 * buffer); this bridge additionally routes each status frame's ``geometry``
 * into :file:`map/geometry-store.ts` so the FoV / coverage / obscuration
 * polygon layers can draw the platform's sensor footprint. Shaped as a
 * prop-driven hook (taking the Map's ``connection.subscribe``) to mirror
 * :func:`map/use-track-sse-bridge` + :func:`detections/use-detections-sse-bridge`
 * and unit-test without a provider.
 *
 * A ``status`` frame with ``geometry: null`` (or absent — older BFF) clears
 * that platform's entry: a platform that stops reporting drawable geometry
 * drops its polygons (see :func:`geometry-store.upsert`). Other message
 * kinds (``detection`` / ``alert`` / ``alert-dismissed``) are no-ops.
 *
 * Every frame reaches this bridge, including several in one flush (issue
 * #739). The single-slot ``lastMessage`` this used to read looked harmless
 * here because geometry is latest-wins *per platform* — but the slot was
 * global, not per-platform, so a flush carrying frames for platforms A, B
 * and C delivered only C's, leaving A's and B's footprints stale until
 * their next frame.
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry) / REQ-UI-006 (live update latency).
 *   - REQ-QUERY-027 (status geometry surface) / ADR-032 §4.2.
 *   - map/sse-subscription.ts (the fan-out) · map/geometry-store.ts.
 */
import { useSseMessages, type SseSubscribe } from "@/map/sse-subscription";
import { useGeometryStore } from "@/map/geometry-store";

/**
 * Route every arriving ``status``-kind SSE frame's ``geometry`` into the
 * geometry store. No-op for every other message kind. A ``null``/absent
 * geometry clears the platform's entry.
 *
 * @param subscribe - The Map's ``connection.subscribe``. ``null`` mounts no
 *     subscription (no connection yet).
 */
export function useStatusGeometrySseBridge(subscribe: SseSubscribe | null): void {
  useSseMessages(subscribe, (message) => {
    if (message.kind === "status") {
      const payload = message.payload;
      useGeometryStore.getState().upsert(payload.producer_id, payload.geometry ?? null);
    }
  });
}
